import { getCurrentUser } from '@features/auth/store/auth.selectors';
import IconEmptyPage from '@features/common/components/IconEmptyPage';
import Listing from '@features/common/components/Listing';
import GameplayRankingGridRow from '@features/gameplay/components/ranking/GameplayRankingGridRow';
import { ITournamentParticipantRanking } from '@features/gameplay/models/tournament-participant-ranking.model';
import LoadingIndicator from '@features/layout/components/LoadingIndicator';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

interface IRankingGridProps {
	type: 'CURRENT_GAME_RANKING' | 'LEADERBOARD' | 'CHAMPIONSHIPS';
	showAwards: boolean;
	onLoadMore: (skip: number, take: number) => Promise<any>;
	participants: Omit<ITournamentParticipantRanking, 'id' | 'tournamentParticipantId'>[];
	totalParticipants: number | null;
}

const GameplayRankingGrid = ({ type, showAwards, onLoadMore, participants, totalParticipants }: IRankingGridProps) => {
	const { formatMessage } = useIntl();
	const currentUser = useSelector(getCurrentUser);

	const getHeaderTextStyle = (): string => {
		if (type === 'CHAMPIONSHIPS' || type === 'LEADERBOARD') {
			return 'font-bold text-dark-600 mb-4 md:mb-8';
		}
		return 'text-grayText-500';
	};

	const getHeaderTextShadow = (): string => {
		if (type === 'CURRENT_GAME_RANKING') {
			return '0px 4px 4px rgba(0, 0, 0, 0.44)';
		}

		return '';
	};

	const getTitleContainerStyle = () => {
		if (type === 'CURRENT_GAME_RANKING' || type === 'LEADERBOARD') {
			return 'pl-4 pr-4 md:pr-4 lg:px-5';
		}
		return '';
	};

	const getMainContainerStyle = () => {
		if (type === 'CURRENT_GAME_RANKING' || type === 'LEADERBOARD') {
			return 'px-2';
		}
		return '';
	};

	const getSizeOfMainContainer = () => {
		if (showAwards) {
			return 'lg:w-5/12';
		}
		return 'lg:w-1/2';
	};

	const getSizeOfInfoContainer = () => {
		if (showAwards) {
			return 'lg:w-7/12';
		}
		return 'lg:w-1/2';
	};

	const getSpaceBetweenHeaders = () => {
		if (type === 'CHAMPIONSHIPS') {
			return 'mr-[18px]';
		}
		return '';
	};

	const getSpaceForLongNameAvgTime = () => {
		if (showAwards) {
			return 'lg:px-1';
		}
		return '';
	};

	if (!participants) {
		return (
			<div className='h-full w-full flex flex-col justify-center items-center'>
				<LoadingIndicator contained={true} />
			</div>
		);
	}

	return (
		<div className={`flex flex-col flex-1 overflow-hidden ${getMainContainerStyle()}`}>
			<div className={`w-full flex text-left ${getTitleContainerStyle()}`}>
				<p
					className={`w-3/4 ${getSizeOfMainContainer()} text-xs md:text-xs uppercase ${getHeaderTextStyle()}`}
					style={{ textShadow: getHeaderTextShadow() }}
				>
					{formatMessage({ id: 'bcs-Gameplay-Position' })} & {formatMessage({ id: 'bcs-Gameplay-Name' })}
				</p>
				<div className={`w-1/4 flex ${getSizeOfInfoContainer()}`}>
					<p
						className={`hidden lg:block w-1/3 text-2xs md:text-xs uppercase ${getHeaderTextStyle()}`}
						style={{ textShadow: getHeaderTextShadow() }}
					>
						{formatMessage({ id: 'bcs-Gameplay-Correct' })}
					</p>
					<p
						className={`hidden lg:block w-1/3 text-2xs md:text-xs uppercase ${getSpaceForLongNameAvgTime()} ${getHeaderTextStyle()}`}
						style={{ textShadow: getHeaderTextShadow() }}
					>
						{formatMessage({
							id: 'bcs-Gameplay-AverageAnswerTime',
						})}
					</p>
					<p
						className={`lg:w-1/3 text-xs md:text-xs uppercase ${getHeaderTextStyle()}`}
						style={{ textShadow: getHeaderTextShadow() }}
					>
						{formatMessage({ id: 'bcs-Gameplay-Score' })}
					</p>
					<p
						className={`hidden lg:block w-2/6 lg:w-full text-2xs md:text-xs uppercase ${getSpaceBetweenHeaders()} ${getHeaderTextStyle()} ${
							showAwards ? '' : 'lg:hidden'
						}`}
						style={{ textShadow: getHeaderTextShadow() }}
					>
						{formatMessage({ id: 'bcs-Gameplay-Award' })}
					</p>
				</div>
			</div>
			<div
				className={`flex-1 ${
					type === 'CURRENT_GAME_RANKING' || type === 'CHAMPIONSHIPS'
						? ' lg:max-h-full lg:overflow-y-hidden '
						: ''
				}`}
			>
				{participants.length ? (
					<Listing
						hasMoreData={participants.length !== totalParticipants}
						skip={20}
						take={20}
						loadMore={onLoadMore}
					>
						{participants.map((participant, index) => (
							<GameplayRankingGridRow
								isSelected={
									currentUser?.id === participant.customerId && type === 'CURRENT_GAME_RANKING'
								}
								position={index + 1}
								participant={participant}
								type={type}
								showAwards={showAwards}
								key={participant.customerNickname}
							/>
						))}
					</Listing>
				) : (
					<IconEmptyPage />
				)}
			</div>
		</div>
	);
};

export default GameplayRankingGrid;
